import { getCustomerCategory, getSponsorUnicityId } from "./helper-functions";

// ─────────────────────────────────────────────────────────────────────────────
// Who can open a MEMBER's profile (GO-3345 round 2, PM call 2026-08-05):
// exactly the top two levels of the user's own organisation, and only through
// another member —
//   1. the user's own frontline members  (the member's sponsor IS the user)
//   2. members sponsored by one of those frontline members
// Anything deeper is blocked, regardless of the sponsor's account type;
// partners are viewable at any depth and never come through these functions.
//
// This module exists so the rule has ONE home. MembersDrawer decides with it
// reactively; RankQualBox (GO-3366) asks the same question imperatively at
// click time. It deliberately does NOT serve PartnersNameCell, whose
// clickability legitimately also branches on page context (search results,
// page-owner type) — see GO-3366's "considered and rejected" note.
// ─────────────────────────────────────────────────────────────────────────────

// The pages where MembersDrawer denies out-of-scope members — the one home
// for this predicate, shared with the drawer and the click-time gate.
const PARTNERS_PAGE_PATH = /^\/partners(\/|$)/;

// `?.` on toString: unicityId comes straight off the customers API and can be
// a JSON null, which passes the `!== undefined` guard — the pre-refactor
// drawer was null-safe here and denied; a bare .toString() would throw.
const isDirectSponseeOfUser = (memberSponsorId, userUnicityId) =>
  memberSponsorId !== undefined &&
  userUnicityId !== undefined &&
  memberSponsorId === userUnicityId?.toString();

// The explicit undefined checks matter: a failed grandparent lookup leaves
// sponsorSponsorId undefined, and a half-loaded session leaves userUnicityId
// undefined — without them two unknowns would compare equal and open the
// profile. (Same reasoning as MembersDrawer round 2.)
const isSponseeOfOwnMember = ({ sponsorType, sponsorSponsorId, userUnicityId }) =>
  sponsorType === "member" &&
  sponsorSponsorId !== undefined &&
  userUnicityId !== undefined &&
  sponsorSponsorId === userUnicityId?.toString();

const isMemberViewableByUser = ({
  memberSponsorId,
  sponsorType,
  sponsorSponsorId,
  userUnicityId,
}) =>
  isDirectSponseeOfUser(memberSponsorId, userUnicityId) ||
  isSponseeOfOwnMember({ sponsorType, sponsorSponsorId, userUnicityId });

// Imperative form of the check, for click-time callers that already hold the
// target's expanded customer record (with a sponsor field). Performs the same
// up-to-two lookups useSponsorType makes reactively, against the same
// endpoints, and FAILS CLOSED on any lookup error — matching the drawer, which
// resolves its check as denied rather than spinning or guessing.
//
// Returns true (viewable) / false (the drawer would deny).
const resolveMemberViewable = async ({
  targetCustomerRecord,
  userUnicityId,
  sendRequest,
}) => {
  const memberSponsorId = getSponsorUnicityId(targetCustomerRecord);
  if (isDirectSponseeOfUser(memberSponsorId, userUnicityId)) {
    return true;
  }
  // `== null` (not `=== undefined`): the session id can be a JSON null, which
  // is just as unknowable — deny without spending the lookups.
  if (memberSponsorId === undefined || userUnicityId == null) {
    return false; // unknowable → closed, like the drawer's failed-lookup path
  }
  try {
    const sponsorRes = await sendRequest({
      method: "get",
      endpoint: `customers?unicity=${memberSponsorId}&expand=customer`,
    });
    const sponsor = sponsorRes.data?.items?.[0];
    // A partner sponsor already decides the answer — a distributor between
    // the user and the member blocks it however close that distributor is.
    if (getCustomerCategory(sponsor?.type) !== "member" || !sponsor?.href) {
      return false;
    }
    const sponsorHrefRes = await sendRequest({
      method: "get",
      addPrefix: false,
      endpoint: `${sponsor.href}?expand=profilePicture`,
    });
    return isSponseeOfOwnMember({
      sponsorType: "member",
      sponsorSponsorId: getSponsorUnicityId(sponsorHrefRes.data),
      userUnicityId,
    });
  } catch (_error) {
    return false;
  }
};

export {
  PARTNERS_PAGE_PATH,
  isDirectSponseeOfUser,
  isSponseeOfOwnMember,
  isMemberViewableByUser,
  resolveMemberViewable,
};
